import { cn } from '@/lib/utils';

/** Logo corporativo oficial horizontal en blanco (transparente). */
const BRAND_LOGO_LIGHT = '/brand/oro-express-horizontal.png';
/** Logo vertical original (texto oscuro) — solo para fondos claros. */
const BRAND_LOGO_DARK = '/brand/oro-express-logo.svg';

interface OroExpressWordmarkProps {
  className?: string;
  variant?: 'full' | 'compact' | 'mini';
  /** Texto opcional bajo el logo. */
  tagline?: string;
  /**
   * `dark` (default) usa el logo horizontal blanco sobre fondo oscuro.
   * `light` usa el logo vertical original (texto oscuro) sobre fondos claros
   * (PDFs de certificado, etc.).
   */
  surface?: 'dark' | 'light';
}

/**
 * Marca Oro Express usando el logo oficial. Servimos `<img>` directamente
 * (no next/image) porque next/image bloquea SVG arbitrario y para PNG
 * pequeños no aporta gran cosa.
 */
export function OroExpressWordmark({
  className,
  variant = 'full',
  tagline,
  surface = 'dark',
}: OroExpressWordmarkProps) {
  // Logo horizontal: ancho > alto. Usamos altura como guía y dejamos que
  // el ancho fluya natural (object-contain). Tamaños calibrados para el
  // ratio aproximado del horizontal (≈3:1).
  const maxHeights = {
    full: 'h-12 sm:h-14',
    compact: 'h-9',
    mini: 'h-7',
  } as const;

  const isLight = surface === 'light';

  return (
    <div className={cn('flex flex-col items-start gap-2', className)}>
      <div className="inline-flex items-center justify-center">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={isLight ? BRAND_LOGO_DARK : BRAND_LOGO_LIGHT}
          alt="Oro Express"
          width={isLight ? 248 : 720}
          height={isLight ? 286 : 240}
          loading={variant === 'full' ? 'eager' : 'lazy'}
          className={cn('w-auto object-contain', maxHeights[variant])}
        />
      </div>
      {tagline ? (
        <span className="text-[10px] uppercase tracking-[0.25em] text-cream/45">
          {tagline}
        </span>
      ) : null}
    </div>
  );
}
